/**
 * MetadataDisplayManager
 * Reads payment description and reference data from the current settings and
 * delivers the result to the consumer event hook and to the metadata-display
 * fragment via the command dispatcher.
 *
 * This class is not exposed to the consuming application.
 */

import { createLogger } from '@anddone/ajs-logger';
import type { CommandDispatcher, ReferenceDataList } from '@anddone/ajs-util';
import type { Events } from '../Events';
import type { SettingsState } from './SettingsState';

const logger = createLogger('SDK', 'MetadataDisplayManager');

export class MetadataDisplayManager {
  // Most-recently published metadata; null until first published.
  #currentPaymentDescription: string | null = null;
  #currentReferenceDataList: readonly ReferenceDataList[] | null = null;

  constructor(
    private readonly settings: SettingsState,
    private readonly dispatcher: CommandDispatcher,
    private readonly events: Events,
  ) {}

  /**
   * Most-recently published payment description, or null until first published.
   */
  public get currentPaymentDescription(): string | null {
    return this.#currentPaymentDescription;
  }

  /**
   * Most-recently published reference data list, or null until first published.
   */
  public get currentReferenceDataList(): readonly ReferenceDataList[] | null {
    return this.#currentReferenceDataList;
  }

  /**
   * Read payment description and reference data from settings, emit
   * `metadata-display-change`, and send `update-metadata-display` to the
   * metadata-display fragment.
   *
   * Returns early (with a debug log) when settings have not been loaded yet or
   * when neither field is present on the settings.
   */
  public publish(): void {
    const settings = this.settings.current;

    if (!settings) {
      logger.debug('publish', 'Skipped — settings not yet available.');
      return;
    }

    const paymentDescription = settings.paymentDescription ?? '';
    const referenceDataList = settings.referenceDataList ? [...settings.referenceDataList] : [];

    if (!settings.paymentDescription && !settings.referenceDataList) {
      logger.debug('publish', 'Skipped — no paymentDescription or referenceDataList in settings.');
      return;
    }

    this.#currentPaymentDescription = paymentDescription;
    this.#currentReferenceDataList = referenceDataList;

    logger.info('publish', 'Publishing metadata display.', {
      paymentDescription,
      referenceDataListCount: referenceDataList.length,
    });

    this.events.emit('metadata-display-change', {
      paymentDescription,
      referenceDataList,
    });

    const metadataPayload = { paymentDescription, referenceDataList };

    // Dispatch to the standalone metadata-display fragment as well as each full-checkout
    // variant that embeds metadata display inline. The dispatcher skips targets that are
    // not currently registered, so it is safe to list all targets here unconditionally.
    this.dispatcher.dispatch('metadata-display', 'update-metadata-display', metadataPayload);
    this.dispatcher.dispatch('full-card-checkout', 'update-metadata-display', metadataPayload);
    this.dispatcher.dispatch('full-ach-checkout', 'update-metadata-display', metadataPayload);
    this.dispatcher.dispatch(
      'full-multi-channel-checkout',
      'update-metadata-display',
      metadataPayload,
    );
  }
}

Object.freeze(MetadataDisplayManager.prototype);
